/* eslint-disable max-lines */
import { v4 as uuidv4 } from "uuid";
import _ from "lodash";
import cn from "classnames";
import React, { useContext, memo } from "react";
import AppContext from "../../AppContext";
import { PriceSummary } from "../PriceSummary";
import {
  getCurrentOrder,
  getProductForNumber,
  getDeliveryPeriodForProduct,
  getProductSizesForNumber,
  getProductColorsForNumber,
  getProductFrontImageForNumber,
  getCollInfoForProduct,
  mapDataWithLenghtsToDataWithColors,
  getPriceForSizeForProductAndColor,
  getProductPriceSummary,
  getTischOffensiveColors,
  getTischOffensivesForId,
} from "../../selectors";
import {
  ProductImageForCart,
  ProductSmallDescription,
  FlexBox,
  SpacingBox,
  CloseIcon,
  WithConfirm,
  InfoCaption,
  EditIcon,
  BorderlessInfoDropdown,
  OpenContentIcon,
  CloseContentIcon,
  CopyIcon,
  BoldCaption,
  RedParagraph,
} from "../../Components";
import {
  mapDeliveryPeriods,
  imageUrlForList,
  requiresMinQuantity,
} from "../../utils/products";
import { getTranslateByScope } from "../../i18n";
import OrderProductMatrix from "./OrderProductMatrix";
import OrderProductDescription from "./OrderProductDescription";
import QuantitiesBox from "./QuantitiesBox";
import TotalQuantityText from "./TotalQuantityText";
import TotalPriceText from "./TotalPriceText";
import CalculatePrices from "./OrderProductMatrix/CalculatePrices";
import { isOrderEditable } from "../../utils/order";
import styles from "./Product.module.scss";
import { ProductPresenter } from "../../presenters";

const translate = getTranslateByScope("app.currentOrder");

export const EditProductContainer = memo(
  ({
    product,
    deliveryDate,
    notes,
    sizes,
    colors,
    total,
    currency,
    id,
    productSeason,
  }) => (
    <SpacingBox marginTop="md">
      <div>
        <OrderProductMatrix
          key={deliveryDate ? deliveryDate.value : null}
          deliveryDate={deliveryDate ? deliveryDate.value : null}
          product={product}
          colors={colors}
          sizes={sizes}
          currency={currency}
          total={total}
          id={id}
          productSeason={productSeason}
        />
      </div>
      <SpacingBox marginTop="sm">
        <OrderProductDescription id={id} notes={notes} />
      </SpacingBox>
    </SpacingBox>
  ),
);

function Trigger(props) {
  const images = props.images;
  const product = props.product;
  const deliveryDate = props.deliveryDate;
  const collInfo = props.collInfo;
  const collInfoSymbols = props.collInfoSymbols;
  const colors = props.colors;
  const mappedData = props.mappedData;
  const currency = props.currency;
  const total = props.total;
  const productSummary = props.productSummary;
  const isOpen = props.isOpen;
  const deliveryPeriodsMapped = props.deliveryPeriodsMapped;
  const updateDeliveryDate = props.updateDeliveryDate;

  return (
    <>
      <div className="row" style={{ marginTop: "10px" }}>
        <div className="col-12 col-xl-7 col-lg-7">
          <FlexBox style={{ height: "100%" }} alignItems="flex-start">
            <FlexBox>
              <ProductImageForCart
                img={imageUrlForList(images.front)}
                imgOnHover={imageUrlForList(images.back)}
              />
            </FlexBox>

            <FlexBox flexDirection="column">
              <ProductSmallDescription
                product={product}
                deliveryDate={deliveryDate}
              />
              {collInfo && (
                <div style={{ paddingLeft: "8px", position: "relative" }}>
                  {_.map(collInfoSymbols, (symbol) => (
                    <img
                      width="32px"
                      height="32px"
                      key={JSON.stringify(symbol)}
                      alt={symbol}
                      style={{ paddingRight: "3px" }}
                      src={`https://assets-crm.schoeffel.de/public/images/symbols/${symbol}.png`}
                    />
                  ))}
                </div>
              )}
            </FlexBox>
          </FlexBox>
        </div>
        <div className="col-12 col-xl-5 col-lg-5">
          <div className="row">
            <div className="col-6">
              <FlexBox flexDirection="column">
                <BoldCaption>{translate("colors")}</BoldCaption>
                <FlexBox style={{ maxWidth: 220 }} flexWrap="wrap">
                  <QuantitiesBox data={mappedData} colors={colors} />
                </FlexBox>
              </FlexBox>
            </div>
            <div className="col-6">
              <FlexBox flexDirection="row" justifyContent="space-between">
                <div>
                  <BoldCaption>{translate("quantity")}</BoldCaption>
                  <TotalQuantityText data={mappedData} />
                </div>
                <div>
                  <BoldCaption>{translate("total")}</BoldCaption>
                  <TotalPriceText currency={currency} total={total} />
                </div>
              </FlexBox>
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <BoldCaption>{translate("prices")}</BoldCaption>
              <PriceSummary values={productSummary} currency={currency} />
            </div>
          </div>
        </div>
      </div>

      <div
        className={cn(
          styles.deliveryDateboxClosed,
          isOpen ? styles.deliveryDateboxOpen : "",
        )}
      >
        <FlexBox
          marginTop="lg"
          marginLeft="md"
          alignItems="center"
          style={{ height: 40 }}
        >
          <SpacingBox marginRight="sm">
            <EditIcon size="xs" />
          </SpacingBox>
          <InfoCaption>{translate("deliveryDate")}</InfoCaption>
          <BorderlessInfoDropdown
            options={deliveryPeriodsMapped}
            placeholder={translate("deliveryDatePlaceholder")}
            onChange={updateDeliveryDate}
            value={deliveryDate}
            noOptionsMessage={() => translate("noDeliveryDateFound")}
            firstSelectedAsDefault
          />
        </FlexBox>
      </div>
      {!deliveryDate && (
        <div className="row">
          <div className="col-12">
            <RedParagraph>{translate("missingDeliveryDate")}</RedParagraph>
          </div>
        </div>
      )}
    </>
  );
}

const ProductTitle = ({ order, product, sizes }) => {
  const orderType = order.orderType.value;

  if (orderType === undefined) {
    return <div>error</div>;
  }

  const containsMinQuantity = requiresMinQuantity(
    product.cluster,
    product.collection,
    sizes,
    orderType,
  );

  return (
    <span>
      <b>{product.name}</b>
      {containsMinQuantity ? (
        <span style={{ color: "red", fontWeight: "bold" }}>
          {" "}
          - {translate("minQuantity")}
        </span>
      ) : null}
    </span>
  );
};

export const Product = ({
  productNumber,
  productSeason,
  deliveryDate,
  tischOffensiveId,
  notes,
  setOpen,
  open,
  data,
  id,
}) => {
  const context = useContext(AppContext);
  const p = getProductForNumber(context)(productNumber);
  if (!p) return <div>no product</div>;

  const product = ProductPresenter(p);

  const order = getCurrentOrder(context) ? getCurrentOrder(context).doc : null;
  const deliveryPeriods = getDeliveryPeriodForProduct(context)({
    productNumber,
  });

  const deliveryPeriodsMapped = mapDeliveryPeriods(deliveryPeriods);

  const updateDeliveryDate = (_deliveryDate) => {
    context.actions.onAddOrUpdateOrderProduct({
      order,
      product: {
        id,
        deliveryDate: _deliveryDate,
        data,
      },
      onSuccess: () => {},
    });
  };

  const allColors = getProductColorsForNumber(context)(product.number);
  let colors = allColors;
  //  const colorCodes = allColors.map((c) => c.code);

  if (tischOffensiveId) {
    const tischOffensive = getTischOffensivesForId({
      context,
      id: tischOffensiveId,
    });
    const name = tischOffensive.description;
    product.tischOffensiveName = name;

    const tischOffensiveColors = getTischOffensiveColors({
      context,
      season: productSeason,
      id: tischOffensiveId,
    });
    colors = allColors.filter((a) =>
      (tischOffensiveColors[product.number] ?? []).includes(a.code),
    );
  }

  colors = colors.sort();
  const firstColor = colors[0];

  const images = getProductFrontImageForNumber(context)(
    product.number,
    firstColor,
  );
  const sizes = getProductSizesForNumber(context)(product.number);

  const mappedData = mapDataWithLenghtsToDataWithColors(data);
  const { total, currency } = CalculatePrices({
    colors: colors.map((c) => c.code),
    number: productNumber,
    sizes,
    data,
    season: productSeason,
  });

  const isOpen = open.includes(id);

  const { pricesForSizes } = getPriceForSizeForProductAndColor(
    sizes,
    null,
    context,
    colors[0].code,
    product,
    getCurrentOrder(context),
  );

  const productSummary = getProductPriceSummary({
    sizes: _.keys(pricesForSizes),
    pricesForSizes,
  });

  const collInfo = getCollInfoForProduct(context, product);
  const collInfoSymbols = _.uniq(_.map(_.get(collInfo, "symbols"), (c) => c));
  const isReadonly = !isOrderEditable(order);

  const currentOrder = getCurrentOrder(context);

  const onCopy = () =>
    context.actions.onAddOrUpdateOrderProduct({
      onSuccess: () => {
        context.actions.setToast({
          title: translate("productCopied.title"),
          description: translate("productCopied.description"),
        });
      },
      order: currentOrder.doc,
      product: {
        id: uuidv4(),
        number: productNumber,
        season: productSeason,
        deliveryDate,
        createdAt: new Date(),
      },
    });

  return (
    <SpacingBox marginTop="xl">
      <FlexBox flex={1} justifyContent="space-between">
        <FlexBox
          style={{ cursor: "pointer" }}
          onClick={() =>
            isOpen
              ? setOpen(_.filter(open, (o) => o !== id))
              : setOpen([...open, id])
          }
        >
          <SpacingBox>
            {isOpen ? <OpenContentIcon /> : <CloseContentIcon />}
          </SpacingBox>
          <SpacingBox>
            <ProductTitle order={order} product={product} sizes={sizes} />
          </SpacingBox>
        </FlexBox>
        <FlexBox>
          {!isReadonly && (
            <>
              <SpacingBox marginRight="sm">
                <CopyIcon onClick={onCopy} />
              </SpacingBox>
              <WithConfirm
                onClick={() => {
                  context.actions.removeProductFromOrder({
                    id,
                    order,
                  });
                  context.actions.setToast({
                    title: translate("productRemovedToast.title"),
                    description: translate("productRemovedToast.description"),
                  });
                }}
              >
                {(onClickWithConfirm) => (
                  <CloseIcon onClick={onClickWithConfirm} />
                )}
              </WithConfirm>
            </>
          )}
        </FlexBox>
      </FlexBox>
      <Trigger
        images={images}
        product={product}
        deliveryDate={deliveryDate}
        collInfo={collInfo}
        collInfoSymbols={collInfoSymbols}
        colors={colors}
        mappedData={mappedData}
        currency={currency}
        total={total}
        productSummary={productSummary}
        deliveryPeriodsMapped={deliveryPeriodsMapped}
        updateDeliveryDate={updateDeliveryDate}
        isOpen={isOpen}
      />
      <div
        className={cn(
          styles.collapsed,
          isOpen && deliveryDate ? styles.open : "",
        )}
      >
        {isOpen && (
          <EditProductContainer
            notes={notes}
            deliveryDate={deliveryDate}
            order={order}
            product={product}
            colors={colors}
            sizes={sizes}
            total={total}
            currency={currency}
            id={id}
            productSeason={productSeason}
          />
        )}
      </div>
    </SpacingBox>
  );
};

export default Product;
